import React, { useState, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { SignalSparkline } from './signalSparkline';
import { Signal } from '@catalyst-iq/shared';
import { api } from '../services/api';
import { TickerIcon } from './TickerIcon';


const LABEL_CONFIG: Record<string, { pill: string; dot: string; label: string; rowAccent: string }> = {
  momentum: {
    pill: 'bg-amber-50 dark:bg-amber-400/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-400/25',
    dot: 'bg-amber-400',
    label: 'Momentum',
    rowAccent: 'border-l-2 border-l-amber-400/60',
  },
  catalyst: {
    pill: 'bg-blue-50 dark:bg-[#4F8FF8]/10 text-blue-700 dark:text-[#4F8FF8] border-blue-200 dark:border-[#4F8FF8]/25',
    dot: 'bg-blue-400 dark:bg-[#4F8FF8]',
    label: 'Catalyst',
    rowAccent: 'border-l-2 border-l-[#4F8FF8]/60',
  },
  rebound: { // 🌟 שונה לאדום (ברירת מחדל לסקאנר וטופ גיינרס)
    pill: 'bg-red-50 dark:bg-[#f5476a]/10 text-red-700 dark:text-[#f5476a] border-red-200 dark:border-[#f5476a]/25',
    dot: 'bg-red-400 dark:bg-[#f5476a]',
    label: 'Rebound',
    rowAccent: 'border-l-2 border-l-[#f5476a]/60',
  },
  rebound_green: { // 🌟 תגית חדשה בצבע ירוק במיוחד עבור הטופ לוזרס
    pill: 'bg-emerald-50 dark:bg-[#10D98E]/10 text-emerald-700 dark:text-[#10D98E] border-emerald-200 dark:border-[#10D98E]/25',
    dot: 'bg-emerald-400 dark:bg-[#10D98E]',
    label: 'Rebound',
    rowAccent: 'border-l-2 border-l-[#10D98E]/60',
  },
  none: {
    pill: 'bg-gray-50 dark:bg-[#1D2D47]/50 text-gray-500 dark:text-[#c2d0e0] border-gray-200 dark:border-[#1D2D47]',
    dot: 'bg-gray-400 dark:bg-[#a3b6cc]',
    label: 'Unconfirmed',
    rowAccent: 'border-l-2 border-l-transparent',
  },
};

const TH = ({ children, right, compact }: { children?: React.ReactNode; right?: boolean; compact?: boolean }) => (
  <th className={`${compact ? 'px-2 w-10' : 'px-5'} py-3 text-[10.5px] font-semibold tracking-[0.08em] uppercase text-gray-400 dark:text-[#a3b6cc] ${right ? 'text-right' : 'text-left'}`}>
    {children}
  </th>
);

// Formats an ISO timestamp (e.g. detected_at) as HH:MM:SS — no milliseconds
function formatDetectedTime(iso: string): string {
  const d = new Date(iso);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm_time = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd}/${yy} - ${hh}:${mm_time}`;
}

interface SignalsTableProps {
  signals: Signal[];
  title?: string;
  subtitle?: string;
  topStripGradient: string;
  headerBgClass: string;
  emptyMessage: string;
  isLoading?: boolean;
  isScanning?: boolean;
  /** Bumps when a scan finishes — triggers a brief table refresh flash */
  scanPulse?: number;
  iconNode?: React.ReactNode;
  reboundTooltip?: string;
}

export function SignalsTable({
  signals,
  topStripGradient,
  headerBgClass,
  emptyMessage,
  isLoading,
  isScanning,
  scanPulse = 0,
  iconNode,
  reboundTooltip
}: SignalsTableProps) {
  // FLIP row-reorder animation
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const prevPositions = useRef<Map<string, number>>(new Map());
  const prevTickers = useRef<Set<string>>(new Set());

  // Price/change flash animation — highlights tickers whose values moved since the last scan tick
  const prevValues = useRef<Map<string, { price: number; changePct: number }>>(new Map());
  const [flashMap, setFlashMap] = useState<Map<string, 'up' | 'down'>>(new Map());
  const [justRefreshed, setJustRefreshed] = useState(false);
  const prevScanPulse = useRef(scanPulse);

  useLayoutEffect(() => {
    if (scanPulse > 0 && scanPulse !== prevScanPulse.current) {
      prevScanPulse.current = scanPulse;
      setJustRefreshed(true);
      const timer = setTimeout(() => setJustRefreshed(false), 700);
      return () => clearTimeout(timer);
    }
    prevScanPulse.current = scanPulse;
  }, [scanPulse]);

  useLayoutEffect(() => {
    const nextFlash = new Map<string, 'up' | 'down'>();

    signals.forEach((s) => {
      const price = s.price ?? 0;
      const changePct = s.change_pct ?? 0;
      const prev = prevValues.current.get(s.ticker);

      if (prev && (prev.price !== price || prev.changePct !== changePct)) {
        nextFlash.set(s.ticker, price >= prev.price ? 'up' : 'down');
      }

      prevValues.current.set(s.ticker, { price, changePct });
    });

    if (nextFlash.size > 0) {
      setFlashMap(nextFlash);
      const timer = setTimeout(() => setFlashMap(new Map()), 900);
      return () => clearTimeout(timer);
    }
  }, [signals]);

  useLayoutEffect(() => {
    const newPositions = new Map<string, number>();
    const currentTickers = new Set(signals.map(s => s.ticker));

    rowRefs.current.forEach((el, ticker) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const oldTop = prevPositions.current.get(ticker);

      if (oldTop !== undefined) {
        const diff = oldTop - rect.top;
        if (Math.abs(diff) > 0.5) {
          el.style.transform = `translateY(${diff}px)`;
          el.style.transition = 'none';
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              el.style.transform = '';
              el.style.transition = 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
            });
          });
        }
      } else if (!prevTickers.current.has(ticker) && prevTickers.current.size > 0) {
        // New ticker entering — play slide-in animation
        el.classList.add('animate-row-enter');
        el.addEventListener('animationend', () => el.classList.remove('animate-row-enter'), { once: true });
      }

      newPositions.set(ticker, rect.top);
    });

    prevPositions.current = newPositions;
    prevTickers.current = currentTickers;
  }, [signals]);
  
  const navigate = useNavigate();

  const getLabelCfg = (label: string) => LABEL_CONFIG[label] ?? LABEL_CONFIG['none'];
  const handleWatchlist = async (ticker: string) => {
    try {
      await api.addToWatchlist({ ticker });
      toast.success(`Added ${ticker} to watchlist`, {
        description: 'Ticker is now tracked with live quotes.',
      });
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.error(`${ticker} is already in your watchlist.`);
      } else {
        toast.error('Failed to add ticker to watchlist.');
      }
    }
  };
  return (
    <>
      {/* Main Table Container */}
      <div
        className={`relative rounded-2xl border bg-white dark:bg-[#0E1624] overflow-hidden shadow-sm transition-[border-color,box-shadow] duration-500 ${
          isScanning
            ? 'border-[#4F8FF8]/30 dark:border-[#4F8FF8]/25 shadow-[0_0_0_1px_rgba(79,143,248,0.06),0_8px_24px_rgba(79,143,248,0.08)]'
            : justRefreshed
              ? 'border-emerald-300/40 dark:border-[#10d98e]/25 shadow-[0_0_0_1px_rgba(16,217,142,0.06),0_6px_18px_rgba(16,217,142,0.06)]'
              : 'border-gray-200 dark:border-[#1D2D47]'
        } ${justRefreshed ? 'animate-scan-refresh' : ''}`}
      >
        {/* Full-table scan overlay — soft wash over header + rows */}
        {isScanning && (
          <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden" aria-hidden="true">
            <span className="absolute inset-0 bg-[#4F8FF8]/[0.025] dark:bg-[#4F8FF8]/[0.04]" />
            <span className="absolute inset-y-0 left-0 w-[32%] bg-gradient-to-r from-[#4F8FF8]/[0.09] via-[#4F8FF8]/[0.07] to-transparent animate-scan-sweep" />
            <span className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-[#4F8FF8]/25 via-[#4F8FF8]/45 to-[#4F8FF8]/25 shadow-[0_0_8px_1px_rgba(79,143,248,0.18)] animate-scan-beam" />
            <span className="absolute inset-0 ring-1 ring-inset ring-[#4F8FF8]/12 dark:ring-[#4F8FF8]/15 animate-scan-ring" />
          </div>
        )}

        <div className={`relative h-[3px] w-full overflow-hidden bg-gradient-to-r ${topStripGradient}`}>
          {isScanning && (
            <span
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-scan-strip pointer-events-none"
              aria-hidden="true"
            />
          )}
        </div>

        <div className={`relative flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-[#1D2D47] overflow-hidden ${headerBgClass}`}>
          <span className="text-[11px] font-mono font-semibold text-gray-400 dark:text-[#a3b6cc]">
            {isLoading ? 'Loading...' : `${signals.length} results`}
          </span>

          {isScanning ? (
            <span className="flex items-center gap-1.5 text-[10px] font-mono font-semibold tracking-wider uppercase text-[#4F8FF8]">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#4F8FF8] opacity-75 animate-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#4F8FF8]" />
              </span>
              Scanning
            </span>
          ) : justRefreshed ? (
            <span className="flex items-center gap-1.5 text-[10px] font-mono font-semibold tracking-wider uppercase text-emerald-600 dark:text-[#10d98e]">
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500 dark:bg-[#10d98e]" />
              Updated
            </span>
          ) : null}
        </div>

        <div className="relative table-scroll">
          <table className="w-full text-[13px] border-collapse min-w-[1100px]">
            <thead>
              <tr className="border-b border-gray-100 dark:border-[#1D2D47]/70 bg-gray-50/50 dark:bg-[#0b111c]">
                <TH compact />
                <TH>Ticker</TH>
                <TH right>Industry</TH>
                <TH right>Price</TH>
                <TH right>Change</TH>
                <TH right>Today</TH>
                <TH right>Rel Vol</TH>
                <TH right>Volume</TH>
                <TH>Label</TH>
                <TH>Time</TH>
                <TH>Reason</TH>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                /* Week 5 Loading Skeleton State */
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={`skeleton-${idx}`} className="border-b border-gray-50 dark:border-[#1D2D47]/40">
                    <td colSpan={11} className="px-5 py-4">
                      <div className="h-4 bg-gray-100 dark:bg-[#1D2D47]/30 rounded animate-pulse w-full" />
                    </td>
                  </tr>
                ))
              ) : signals.length > 0 ? (
                /* Happy Path Data Rendering */
                signals.map((s, i) => {
                  const cfg = getLabelCfg(s.label);
                  const changePct = s.change_pct ?? 0;
                  const isPos = changePct >= 0;
                  const isEven = i % 2 === 0;
                  const flashDir = flashMap.get(s.ticker);
                  const flashClass = flashDir === 'up'
                    ? 'animate-flash-up'
                    : flashDir === 'down'
                      ? 'animate-flash-down'
                      : '';

                  return (
                    <tr
                      key={s.ticker}
                      onClick={() => navigate(`/signal/${s.ticker}`)} // <-- ניווט בלחיצה על כל השורה
                      className={`
                        group border-b border-gray-50 dark:border-[#1D2D47]/40 last:border-0
                        transition-colors duration-100 ${cfg.rowAccent}
                        ${isEven ? '' : 'bg-gray-50/30 dark:bg-[#080C14]/20'}
                        hover:bg-blue-50/40 dark:hover:bg-[#4F8FF8]/5
                        cursor-pointer
                      `}
                    >
                      <td className="px-2 py-3.5">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleWatchlist(s.ticker);
                          }}
                          className="p-1 rounded-md text-gray-300 dark:text-[#a3b6cc] hover:text-amber-400 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-400/10 transition-all duration-150"
                          title="Add to watchlist"
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                          </svg>
                        </button>
                      </td>

                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <TickerIcon ticker={s.ticker} size="sm" />
                          <span className="font-semibold font-mono text-[#4F8FF8] group-hover:text-[#6366F1] transition-colors duration-150 tracking-wide">
                            {s.ticker}
                          </span>
                        </div>
                      </td>

                      <td className="px-5 py-3.5 text-right text-[12px] text-gray-600 dark:text-[#c2d0e0]">
                        {(s as any).industry && (s as any).industry !== 'Unknown' ? (s as any).industry : '-'}
                      </td>
                      <td className={`px-5 py-3.5 text-right font-mono font-medium text-gray-800 dark:text-[#d5deea] tabular-nums transition-colors duration-150 ${flashClass}`}>
                        ${(s.price ?? 0).toFixed(2)}
                      </td>

                      <td className={`px-5 py-3.5 text-right font-mono font-semibold tabular-nums transition-colors duration-150 ${flashClass} ${isPos ? 'text-emerald-600 dark:text-[#10d98e]' : 'text-red-500 dark:text-[#f5476a]'
                        }`}>
                        <span className="inline-flex items-center justify-end gap-0.5">
                          {isPos ? '▲' : '▼'}
                          {Math.abs(changePct).toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-5 py-3.5 align-middle">
                        <SignalSparkline
                          chartData={(s as any).chart_data || []}
                          currentPrice={s.price ?? 0}
                          changePct={changePct}
                        />
                      </td>

                      <td className="px-5 py-3.5 text-right font-mono tabular-nums text-gray-600 dark:text-[#c2d0e0]">
                        {s.rel_volume.toFixed(1)}
                        <span className="text-[10px] text-gray-400 dark:text-[#a3b6cc]">×</span>
                      </td>

                      <td className="px-5 py-3.5 text-right font-mono tabular-nums text-gray-600 dark:text-[#c2d0e0]">
                        {(s.volume / 1_000_000).toFixed(1)}
                        <span className="text-[10px] text-gray-400 dark:text-[#a3b6cc]">M</span>
                      </td>

                      <td className="px-5 py-3.5">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border ${cfg.pill}`}
                          title={(s.label as string) === 'rebound_green' && reboundTooltip ? reboundTooltip.replace('{N}', Math.abs(changePct).toFixed(2)) : ""}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                          {cfg.label}
                        </span>
                      </td>

                      <td className="px-5 py-3.5 whitespace-nowrap">
                        {s.detected_at && (
                          <span className="font-mono text-[11px] text-gray-400 dark:text-[#a3b6cc] tabular-nums">
                            {formatDetectedTime(s.detected_at)}
                          </span>
                        )}
                      </td>

                      <td className="px-5 py-3.5 text-gray-600 dark:text-[#c2d0e0] max-w-[220px]">
                        <span className="block truncate" title={s.label_reason}>{s.label_reason}</span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={11} className="px-5 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-[#141E30] flex items-center justify-center">
                        <svg className="w-5 h-5 text-gray-300 dark:text-[#a3b6cc]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                      </div>
                      <p className="text-[13px] text-gray-400 dark:text-[#a3b6cc]">{emptyMessage || 'No signals match current filters'}</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

